/**
 * Async Job API Client for Frontend
 * Handles submitting long-running jobs and polling for their completion
 *
 * Usage:
 *   import { asyncJobApi } from './services/asyncJobApi';
 *
 *   // Submit a job and wait for completion
 *   const result = await asyncJobApi.submitAndWait('generateDialogueSceneImage', {
 *     actionId: 123,
 *     sceneType: 'external'
 *   }, {
 *     onProgress: (progress, message) => console.log(`${progress}%: ${message}`)
 *   });
 */

import { API_BASE_URL } from '../config/api';

// Job statuses (match backend JobService)
export const JobStatus = {
  PENDING: 'pending',
  PROCESSING: 'processing',
  COMPLETE: 'complete',
  ERROR: 'error',
};

class AsyncJobApiClient {
  constructor(baseUrl = API_BASE_URL) {
    this.baseUrl = baseUrl;
  }

  /**
   * Submit an async job
   * @param {string} operation - Operation name (e.g., 'generateDialogueSceneImage')
   * @param {Object} params - Operation parameters
   * @returns {Promise<{jobId: string, statusUrl: string}>}
   */
  async submitJob(operation, params = {}) {
    const response = await fetch(`${this.baseUrl}/api/async/submit`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ operation, params })
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to submit job' }));
      throw new Error(error.error || `Failed to submit job: ${response.status}`);
    }

    return response.json();
  }

  /**
   * Get job status
   * @param {string} jobId - Job ID to check
   * @returns {Promise<Object>} Job status object
   */
  async getJobStatus(jobId) {
    const response = await fetch(`${this.baseUrl}/api/job/${jobId}`);

    if (!response.ok) {
      if (response.status === 404) {
        return null;
      }
      const error = await response.json().catch(() => ({ error: 'Failed to get job status' }));
      throw new Error(error.error || `Failed to get job status: ${response.status}`);
    }

    return response.json();
  }

  /**
   * Poll job status until completion
   * @param {string} jobId - Job ID to poll
   * @param {Object} options - Polling options
   * @param {number} options.interval - Poll interval in ms (default: 2000)
   * @param {number} options.timeout - Max time to wait in ms (default: 900000 = 15 min)
   * @param {Function} options.onProgress - Called with (progress, message) on updates
   * @returns {Promise<Object>} Final job status with result
   */
  async pollUntilComplete(jobId, options = {}) {
    const {
      interval = 2000,
      timeout = 900000, // 15 minutes
      onProgress = null,
    } = options;

    const startTime = Date.now();
    let lastProgress = -1;

    while (true) {
      const status = await this.getJobStatus(jobId);

      if (!status) {
        throw new Error(`Job not found: ${jobId}`);
      }

      // Call progress callback if progress changed
      if (onProgress && status.progress !== lastProgress) {
        lastProgress = status.progress;
        onProgress(status.progress, status.message);
      }

      // Check if job is complete
      if (status.status === JobStatus.COMPLETE) {
        return status;
      }

      // Check if job failed
      if (status.status === JobStatus.ERROR) {
        throw new Error(status.error?.message || 'Job failed');
      }

      // Check timeout
      if (Date.now() - startTime > timeout) {
        throw new Error(`Job timed out after ${timeout / 1000} seconds`);
      }

      // Wait before next poll
      await new Promise(resolve => setTimeout(resolve, interval));
    }
  }

  /**
   * Submit a job and wait for completion
   * @param {string} operation - Operation name
   * @param {Object} params - Operation parameters
   * @param {Object} options - Options including polling options
   * @returns {Promise<Object>} Job result
   */
  async submitAndWait(operation, params = {}, options = {}) {
    const { jobId } = await this.submitJob(operation, params);
    const status = await this.pollUntilComplete(jobId, options);
    return status.result;
  }

  /**
   * Start polling a job with callbacks (non-blocking)
   * @param {string} jobId - Job ID to poll
   * @param {Object} callbacks - Callback functions
   * @param {Function} callbacks.onProgress - Called with (progress, message)
   * @param {Function} callbacks.onComplete - Called with (result)
   * @param {Function} callbacks.onError - Called with (error)
   * @param {number} interval - Poll interval in ms (default: 2000)
   * @returns {Function} Stop function to cancel polling
   */
  startPolling(jobId, callbacks, interval = 2000) {
    const { onProgress, onComplete, onError } = callbacks;
    let active = true;
    let timeoutId;

    const poll = async () => {
      if (!active) return;

      try {
        const status = await this.getJobStatus(jobId);

        if (!status) {
          if (onError) onError(new Error(`Job not found: ${jobId}`));
          return;
        }

        if (onProgress) {
          onProgress(status.progress, status.message);
        }

        if (status.status === JobStatus.COMPLETE) {
          if (onComplete) onComplete(status.result);
          return;
        }

        if (status.status === JobStatus.ERROR) {
          if (onError) onError(new Error(status.error?.message || 'Job failed'));
          return;
        }

        // Continue polling
        if (active) {
          timeoutId = setTimeout(poll, interval);
        }
      } catch (error) {
        if (onError) onError(error);
      }
    };

    poll();

    return () => {
      active = false;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }
}

// Export singleton instance
export const asyncJobApi = new AsyncJobApiClient();

// Export class for custom instances
export default AsyncJobApiClient;
